import { authService } from '../services/auth.ts';
import { OrganizationService } from '../services/organization.ts';
import { draftingStyles as sharedStyles } from '../utils/shared-styles.ts';
import './library/ui/app-icon.ts';
import './library/ui/app-button.ts';

export class UserMenu extends HTMLElement {
  private _isOpen = false;
  private _profile: any = null;
  private _orgService = OrganizationService.getInstance();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.addEventListener('click', (e) => e.stopPropagation());
  }

  connectedCallback() {
    this.render();
    this.loadProfile();
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick = (e: MouseEvent) => {
    if (this._isOpen) {
      this.toggleMenu();
    }
  };

  private async loadProfile() {
    try {
      this._profile = await authService.getProfile();
      this.render();
    } catch (e) {
      console.error('Failed to load profile', e);
    }
  }

  public toggleMenu() {
    this._isOpen = !this._isOpen;
    this.render();
  }

  private async handleOrgSwitch(orgId: string) {
    try {
      await authService.switchOrganization(orgId);
      this._isOpen = false;
      this._profile = await authService.getProfile(true);
      this.render();
    } catch (e) {
      console.error('Failed to switch organization', e);
    }
  }

  private async handleCreateOrg() {
    this._isOpen = false;
    this.render();
    
    const currentOrgId = authService.getUser()?.org_id;
    if (currentOrgId) {
        localStorage.setItem('crate_fallback_org_id', currentOrgId);
    }
    
    // Clear any previous onboarding state
    localStorage.removeItem('crate_onboarding_plan');
    localStorage.removeItem('crate_onboarding_modules');
    localStorage.removeItem('crate_onboarding_org_id');

    window.dispatchEvent(new CustomEvent('start-onboarding'));
  }

  private handleLogout() {
    authService.logout();
  }

  private render() {
    if (!this._profile) {
        this.shadowRoot!.innerHTML = '';
        return;
    }

    const currentOrgId = this._profile.org_id;

    this.shadowRoot!.innerHTML = `
      <style>
        ${sharedStyles}
        :host {
          display: block;
          position: fixed;
          bottom: 20px;
          left: 80px;
          z-index: 10000;
        }
        .menu-container {
          background: #0a0a0a;
          border: none;
          width: 250px;
          display: ${this._isOpen ? 'flex' : 'none'};
          flex-direction: column;
          pointer-events: auto;
          position: relative;
          box-shadow: 12px 12px 0px rgba(0,0,0,0.5);
          animation: pop-in 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform-origin: bottom left;
        }

        @keyframes pop-in {
            from { opacity: 0; transform: scale(0.95) translateY(10px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* Hand-drawn Menu Border */
        .menu-container::before {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            border: 1px solid var(--border-sketch);
            pointer-events: none;
            z-index: 10;
            filter: url(#sketchy-strong);
        }

        .menu-header {
          padding: 12px 20px;
          border-bottom: 1px solid var(--border-sketch);
          background: rgba(255, 255, 255, 0.01);
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .menu-header:hover {
            background: rgba(245, 158, 11, 0.03);
        }

        .avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 1px solid var(--accent);
            padding: 2px;
            background: #000;
        }

        .user-meta {
            flex: 1;
            min-width: 0;
        }
        .user-name {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.85rem;
          font-weight: 800;
          color: white;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          letter-spacing: -0.01em;
          text-transform: uppercase;
        }
        .user-email {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.65rem;
          color: var(--accent);
          opacity: 0.7;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .menu-body {
            max-height: 250px;
            overflow-y: auto;
            background: repeating-linear-gradient(
                0deg,
                transparent,
                transparent 29px,
                rgba(255, 255, 255, 0.01) 29px,
                rgba(255, 255, 255, 0.01) 30px
            );
        }

        .menu-section {
          padding: 8px 0;
        }
        .section-title {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.6rem;
          color: var(--accent);
          text-transform: uppercase;
          letter-spacing: 0.2em;
          font-weight: 800;
          opacity: 0.8;
        }

        .section-header {
            padding: 12px 20px 8px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .btn-icon-add {
            background: transparent;
            border: none;
            color: white;
            padding: 4px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
            position: relative;
            width: 24px;
            height: 24px;
        }

        /* Hand-drawn + Button Border */
        .btn-icon-add::before {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            border: 1px solid var(--border-sketch);
            border-radius: 4px; /* Changed from circle to slightly rounded square to match sketchy better */
            filter: url(#sketchy-strong);
            pointer-events: none;
            transition: all 0.2s;
        }

        .btn-icon-add:hover {
            color: black;
        }

        .btn-icon-add:hover::before {
            border-color: #f59e0b;
            background: #f59e0b;
        }

        .org-item {
          padding: 8px 24px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.8rem;
          color: #94a3b8;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.2s;
          position: relative;
        }
        .org-item:hover {
          color: white;
          background: rgba(255, 255, 255, 0.03);
        }
        .org-item.active {
          color: var(--accent);
          font-weight: 700;
        }
        .org-item.active::before {
            content: '>';
            position: absolute;
            left: 12px;
            color: var(--accent);
        }

        .org-name {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .org-settings-btn {
            background: transparent;
            color: rgba(255, 255, 255, 0.4);
            width: 24px;
            height: 24px;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
        }
        .org-settings-btn:hover {
            color: var(--accent);
            transform: rotate(30deg);
        }

        .action-item {
            padding: 14px 24px;
            font-family: 'JetBrains Mono', monospace;
            font-size: 0.85rem;
            color: #94a3b8;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            transition: all 0.2s;
            width: 100%;
            box-sizing: border-box;
        }
        .action-item:hover {
            background: rgba(255, 255, 255, 0.03);
            color: white;
        }
        .action-item.logout {
            color: #f87171;
            font-weight: 800;
        }
        .action-item.logout:hover {
            background: rgba(239, 68, 68, 0.05);
            color: #ef4444;
        }

        .menu-footer {
            border-top: 1px solid var(--border-sketch);
            background: rgba(255, 255, 255, 0.01);
            display: flex;
            justify-content: center;
        }

        .menu-actions {
            border-bottom: 1px solid var(--border-sketch);
        }

        /* Custom Scrollbar */
        .menu-body::-webkit-scrollbar {
          width: 4px;
        }
        .menu-body::-webkit-scrollbar-track {
          background: transparent;
        }
        .menu-body::-webkit-scrollbar-thumb {
          background: var(--border-sketch);
          border-radius: 10px;
        }
        .menu-body::-webkit-scrollbar-thumb:hover {
          background: var(--accent);
        }
      </style>

      <div class="menu-container drafting-grid">
        <div class="menu-header" id="header-profile" data-tooltip="View Profile">
          <img class="avatar" src="https://www.gravatar.com/avatar/${this._profile.gravatar}?d=identicon&s=80" />
          <div class="user-meta">
            <div class="user-name">${this._profile.display_name || this._profile.name || this._profile.details?.display_name || 'Anonymous User'}</div>
            <div class="user-email">${this._profile.email}</div>
          </div>
          <app-icon name="chevron-right" size="sm" style="opacity: 0.3;"></app-icon>
        </div>

        <div class="menu-actions">
            <div class="section-header">
                <span class="section-title">ORGANIZATIONS</span>
                <button class="btn-icon-add" id="btn-create-org" data-tooltip="New Organization">
                    <app-icon name="plus" size="sm"></app-icon>
                </button>
            </div>
        </div>

        <div class="menu-body">
            <div class="menu-section" style="padding-top: 4px; border-bottom: none;">
                ${this._profile.organizations?.map((org: any) => `
                    <div class="org-item ${org.id === currentOrgId ? 'active' : ''}" 
                         data-org-id="${org.id}">
                        <span class="org-name">${org.name}</span>
                        <div class="org-settings-btn" data-settings-id="${org.id}" data-tooltip="Organization Settings">
                            <app-icon name="settings" size="sm"></app-icon>
                        </div>
                    </div>
                `).join('')}
            </div>
        </div>

        <div class="menu-footer">
            <div class="action-item logout" id="menu-logout">
                <app-icon name="logout" size="sm"></app-icon>
                <span style="letter-spacing: 0.1em;">LOGOUT</span>
            </div>
        </div>
      </div>
    `;

    // Add event listeners
    this.shadowRoot!.querySelectorAll('.org-item[data-org-id]').forEach(item => {
      item.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.org-settings-btn')) return; // Ignore if settings clicked

        const orgId = item.getAttribute('data-org-id');
        if (orgId && orgId !== currentOrgId) {
          this.handleOrgSwitch(orgId);
        }
      });
    });

    this.shadowRoot!.querySelectorAll('.org-settings-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const orgId = btn.getAttribute('data-settings-id');
            if (orgId) {
                this._isOpen = false;
                this.render();
                window.dispatchEvent(new CustomEvent('navigate', { 
                    detail: { view: 'organization', params: { orgId } } 
                }));
            }
        });
    });

    this.shadowRoot!.querySelector('#btn-create-org')?.addEventListener('click', () => this.handleCreateOrg());
    this.shadowRoot!.querySelector('#menu-logout')?.addEventListener('click', () => this.handleLogout());
    
    this.shadowRoot!.querySelector('#header-profile')?.addEventListener('click', () => {
        this._isOpen = false;
        this.render();
        window.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'profile' } }));
    });
  }
}

if (!customElements.get('user-menu')) {
  customElements.define('user-menu', UserMenu);
}
